import { useState, useEffect } from 'react';
import Landing from './components/Landing';
import ResultsPage from './components/ResultsPage';
import PricingPage from './components/PricingPage';
import AuthModal from './components/AuthModal';
import { useAuth } from './context/AuthContext';
import { mockVariant } from './data/mockData';

function DNALoader() {
  return (
    <svg width="120" height="200" viewBox="0 0 120 200" className="dna-float">
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const y = 20 + i * 22;
        const phase = (i / 8) * Math.PI * 2;
        const x1 = 20 + Math.sin(phase) * 35;
        const x2 = 100 - Math.sin(phase) * 35;
        const opacity = 0.6 + Math.abs(Math.sin(phase)) * 0.4;
        return (
          <g key={i}>
            <line
              x1={x1} y1={y}
              x2={x2} y2={y}
              stroke="#f93441"
              strokeWidth="2"
              strokeOpacity={opacity * 0.7}
            />
            <circle cx={x1} cy={y} r="5" fill="#f93441" fillOpacity={opacity} />
            <circle cx={x2} cy={y} r="5" fill="#ffffff" fillOpacity={opacity * 0.8} />
          </g>
        );
      })}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => {
        const y1 = 20 + i * 22;
        const y2 = 20 + (i + 1) * 22;
        const phase1 = (i / 8) * Math.PI * 2;
        const phase2 = ((i + 1) / 8) * Math.PI * 2;
        const x1 = 20 + Math.sin(phase1) * 35;
        const x2 = 20 + Math.sin(phase2) * 35;
        const rx1 = 100 - Math.sin(phase1) * 35;
        const rx2 = 100 - Math.sin(phase2) * 35;
        if (i >= 7) return null;
        return (
          <g key={`strand-${i}`}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#f93441" strokeWidth="2.5" strokeOpacity="0.8" />
            <line x1={rx1} y1={y1} x2={rx2} y2={y2} stroke="white" strokeWidth="2.5" strokeOpacity="0.6" />
          </g>
        );
      })}
    </svg>
  );
}

function LoadingScreen({ query }) {
  const [progress, setProgress] = useState(0);
  const steps = [
    'Parsing variant coordinates...',
    'Querying gnomAD population database...',
    'Computing pathogenicity scores...',
    'Retrieving ClinVar submissions...',
    'Applying ACMG/AMP criteria...',
    'Generating MGS composite score...',
  ];
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + 2, 95));
    }, 30);
    const stepInterval = setInterval(() => {
      setStepIdx((s) => (s + 1) % steps.length);
    }, 280);
    return () => {
      clearInterval(interval);
      clearInterval(stepInterval);
    };
  }, []);

  return (
    <div
      style={{ background: '#021f30' }}
      className="fixed inset-0 flex flex-col items-center justify-center z-50"
    >
      <div className="flex flex-col items-center gap-8 max-w-md w-full px-6">
        <DNALoader />

        <div className="text-center">
          <h2 className="text-white text-2xl font-bold mb-1">Analyzing Variant</h2>
          {query && (
            <p className="text-blue-200 font-mono text-sm">{query}</p>
          )}
        </div>

        <div className="w-full">
          <div className="flex justify-between text-xs text-blue-200 mb-2">
            <span>{steps[stepIdx]}</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-100"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(to right, #f93441, #ff6b75)',
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 w-full">
          {['gnomAD', 'ClinVar', 'OMIM', 'AlphaMissense', 'SpliceAI', 'ENIGMA'].map((db) => (
            <div
              key={db}
              className="text-center text-xs text-white/50 py-1.5 px-2 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              {db}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { saveSearch } = useAuth();
  const [page, setPage]         = useState('landing');
  const [query, setQuery]       = useState('');
  const [authOpen, setAuthOpen] = useState(false);

  const handleSearch = (q) => {
    setQuery(q);
    setPage('loading');
    saveSearch(q);
    setTimeout(() => setPage('results'), 1800);
  };

  if (page === 'loading') {
    return <LoadingScreen query={query} />;
  }

  return (
    <>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      {page === 'landing' && (
        <Landing onSearch={handleSearch} onAuthOpen={() => setAuthOpen(true)} onPricing={() => setPage('pricing')} />
      )}
      {page === 'results' && (
        <ResultsPage variant={mockVariant} query={query} onNewSearch={() => setPage('landing')} onAuthOpen={() => setAuthOpen(true)} />
      )}
      {page === 'pricing' && (
        <PricingPage onBack={() => setPage('landing')} onAuthOpen={() => setAuthOpen(true)} />
      )}
    </>
  );
}
